import { useLayoutEffect, useRef, useState } from 'react'

import {
  choosePopoverPlacement,
  type PopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement/choose-popover-placement'

interface UseFixedPopoverPlacementArgs {
  anchorRef?: React.RefObject<HTMLElement | null>
  popoverRef: React.RefObject<HTMLElement | null>
  /** Callers that mount the popover conditionally (rather than passing
   * `isOpen`) can omit this — it defaults to measuring on every mount. */
  isOpen?: boolean
  gap?: number
}

interface FixedPopoverStyle {
  position: 'fixed'
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight?: number
}

interface UseFixedPopoverPlacementResult {
  placement: PopoverPlacement
  /** False until the first measurement lands — callers should render
   * `visibility: hidden` until then to avoid a flash at the wrong spot. */
  isMeasured: boolean
  style: FixedPopoverStyle
}

/**
 * Placement for the `position: fixed` + portal popover family (anchored to a
 * card, escaping any scroll/overflow ancestor). Prefers opening upward
 * (matching today's look on every page that already has room) and flips
 * downward only when the anchor is too close to the top of the viewport.
 */
export const useFixedPopoverPlacement = ({
  anchorRef,
  popoverRef,
  isOpen = true,
  gap = 4,
}: UseFixedPopoverPlacementArgs): UseFixedPopoverPlacementResult => {
  const [result, setResult] = useState<{
    placement: PopoverPlacement
    style: FixedPopoverStyle
  }>({
    placement: 'top',
    style: { position: 'fixed', left: 0, width: 0, bottom: 0 },
  })
  const isMeasuredRef = useRef(false)

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef?.current || !popoverRef.current) return

    const anchorRect = anchorRef.current.getBoundingClientRect()
    const popoverHeight = popoverRef.current.getBoundingClientRect().height
    const viewportHeight = window.innerHeight

    const { placement, maxHeight } = choosePopoverPlacement({
      anchorTop: anchorRect.top,
      anchorBottom: anchorRect.bottom,
      popoverHeight,
      viewportHeight,
      preferred: 'top',
      gap,
    })

    isMeasuredRef.current = true
    setResult({
      placement,
      style: {
        position: 'fixed',
        left: anchorRect.left,
        width: anchorRect.width,
        ...(placement === 'top'
          ? { bottom: viewportHeight - anchorRect.top + gap }
          : { top: anchorRect.bottom + gap }),
        ...(maxHeight !== undefined ? { maxHeight } : {}),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- popoverRef and gap are stable ref-backed/constant inputs, not reactive dependencies; only anchorRef/isOpen should re-trigger the measurement
  }, [anchorRef, isOpen])

  return { ...result, isMeasured: isMeasuredRef.current }
}
